import {useTranslation} from '@aboutyou/i18n';
import {Link, useContactRoute, useHistory, useParams} from '@aboutyou/router';
import {TrackContentPageView} from '@aboutyou/tracking';
import {Button, useShowToast} from '@aboutyou/ui-components';
import React, {useMemo} from 'react';
import styled from 'styled-components';
import {NewsletterLandingPage} from './NewsletterLandingPage';
import {useUnsubscribeFromNewsletterWithToken} from './hooks/useUnsubscribeFromNewsletterWithToken';

export const UnsubscribeLandingPage = () => {
  const showToast = useShowToast();
  const contactRoute = useContactRoute();
  const {t} = useTranslation();
  const history = useHistory();
  const {permission, token} = useParams<{token: string; permission: string}>();

  const errorToastText = useMemo(() => {
    return [
      {
        text: t(
          'Sorry, we are experiencing technical difficulties. Please try again in a moment or get in touch with our customer care team via our ',
        ),
      },
      {
        text: t('contact form'),
        url: contactRoute,
      },
      {text: t(' if the problem persists.')},
    ];
  }, [contactRoute, t]);

  const toastContent = (
    <ToasterTextContainer>
      {errorToastText.map((message, index) =>
        message.url ? (
          <StyledLink key={index} to={message.url}>
            {message.text}
          </StyledLink>
        ) : (
          message.text
        ),
      )}
    </ToasterTextContainer>
  );

  const unsubscribeFromNewsletter = useUnsubscribeFromNewsletterWithToken(
    () => history.push('/remove-subscription-confirmation'),
    () =>
      showToast({
        type: 'ERROR',
        content: toastContent,
      }),
  );

  return (
    <StyledNewsletterLandingPage title={t('Sure you want to leave us?')}>
      <TrackContentPageView pageTitle="unsubscribeLandingPage" />
      <SublineContainer>
        {t('It looks like our newsletter correspondence is coming to an end.')}
      </SublineContainer>
      <TextContainer>
        {t(
          'Should you find yourself missing our updates on current offers, promotions, and outfit suggestions, you may subscribe to our newsletter again at any time.',
        )}
        <br />
        <br />
        <b>{t('Psssst: ')}</b>
        {t('We also send out exclusive discount codes from time to time.')}
      </TextContainer>
      <Button
        variant="primary"
        onClick={() => unsubscribeFromNewsletter(token, permission)}
        data-testid="unsubscribeButton"
      >
        {t('Unsubscribe')}
      </Button>
    </StyledNewsletterLandingPage>
  );
};

const StyledNewsletterLandingPage = styled(NewsletterLandingPage)`
  h2 {
    margin-bottom: 10px;
  }
`;

const SublineContainer = styled.div`
  margin-bottom: 20px;
  font-weight: 700;
`;

const TextContainer = styled.div`
  font-weight: 400;
  margin-bottom: 20px;
`;

const StyledLink = styled(Link)`
  font-weight: 700;
`;

const ToasterTextContainer = styled.div`
  display: inline;
`;
